import React, { useState, useEffect, useRef } from 'react';
import FurnitureNavbar from '../../../components/layout/Nav';
import '../../buyer/profile/profile.scss';
import withAuth from '../../auth/WithAuth';
import defaultAvatar from '../../../assets/DefaultProfile.png';
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaLock,
  FaCamera,
  FaCheck,
  FaExclamationTriangle,
  FaEdit,
  FaTimes,
  FaTrash,
  FaArrowRight,
} from 'react-icons/fa';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import { serverUrlAuth } from '../../../serverUrl';
import { useNavigate } from 'react-router-dom';

const ClientProfile = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    avatar: defaultAvatar,
  });
  const [originalProfile, setOriginalProfile] = useState(profile); // store original data
  const [editable, setEditable] = useState(false);
  const [password, setPassword] = useState({
    current: '',
    new: '',
    confirm: '',
  });
  const [alerts, setAlerts] = useState({ profile: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch user profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await serverUrlAuth.get('users/profile/');
        const data = res.data;
        const initialProfile = {
          name: data.full_name,
          email: data.email,
          phone: data.phone_number || '',
          avatar: data.profile_picture || defaultAvatar,
        };
        setProfile(initialProfile);
        setOriginalProfile(initialProfile);
      } catch (error) {
        console.error(error);
      }
    };
    fetchProfile();
  }, []);

  const toggleEdit = () => setEditable(true);

  const cancelEdit = () => {
    setProfile(originalProfile); // revert changes
    setEditable(false);
  };

  // Navigate to delete account page
  const handleDeleteAccountClick = () => {
    navigate('/delete-account');
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/'))
        return showAlert('profile', 'Please select a valid image', 'error');
      if (file.size > 5 * 1024 * 1024)
        return showAlert('profile', 'Image must be less than 5MB', 'error');

      if (profile.avatar && profile.avatar !== defaultAvatar)
        URL.revokeObjectURL(profile.avatar);
      const imageUrl = URL.createObjectURL(file);
      setProfile((prev) => ({ ...prev, avatar: imageUrl }));
      showAlert('profile', 'Avatar updated locally', 'success');
    }
  };

  const triggerFileInput = () => editable && fileInputRef.current?.click();

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPassword((prev) => ({ ...prev, [name]: value }));
  };

  const showAlert = (type, message, variant = 'success') => {
    setAlerts((prev) => ({ ...prev, [type]: { message, variant } }));
    setTimeout(() => setAlerts((prev) => ({ ...prev, [type]: '' })), 5000);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append('full_name', profile.name);
      formData.append('phone_number', profile.phone);
      if (fileInputRef.current?.files[0]) {
        formData.append('profile_picture', fileInputRef.current.files[0]);
      }

      const res = await serverUrlAuth.patch('users/profile/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const updatedProfile = {
        ...profile,
        avatar: res.data.profile_picture || profile.avatar,
      };

      setProfile(updatedProfile);
      setOriginalProfile(updatedProfile);
      showAlert('profile', 'Profile updated successfully!', 'success');
      setEditable(false);
    } catch (error) {
      console.error(error);
      showAlert('profile', 'Failed to update profile', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (password.new.length < 8)
      return showAlert(
        'password',
        'Password must be at least 8 characters',
        'error',
      );
    if (password.new !== password.confirm)
      return showAlert('password', "Passwords don't match!", 'error');

    setIsLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 1000)); // simulate API
    setIsLoading(false);
    showAlert('password', 'Password changed successfully!', 'success');
    setPassword({ current: '', new: '', confirm: '' });
  };

  return (
    <>
      <FurnitureNavbar />
      <div className="profile-page">
        <div className="profile-layout">
          <div className="vertical-tabs">
            <button
              className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              <FaUser className="tab-icon" /> Profile Information
            </button>
            {/* <button
              className={`tab-btn ${activeTab === 'password' ? 'active' : ''}`}
              onClick={() => setActiveTab('password')}
            >
              <FaLock className="tab-icon" /> Change Password
            </button> */}
            {/* Delete Account Tab - Navigates to /delete-account */}
            <button
              className="tab-btn delete-tab"
              onClick={handleDeleteAccountClick}
            >
              <FaTrash className="tab-icon" /> Delete Account
              <FaArrowRight className="tab-arrow" />
            </button>
          </div>

          <div className="profile-content">
            {alerts.profile && (
              <Alert variant={alerts.profile.variant} className="alert-custom">
                {alerts.profile.variant === 'success' ? (
                  <FaCheck />
                ) : (
                  <FaExclamationTriangle />
                )}
                {alerts.profile.message}
              </Alert>
            )}

            {activeTab === 'profile' && (
              <div className="profile-card">
                <div className="card-header">
                  <h3>Profile Information</h3>
                  <p>Update your personal details and avatar</p>
                </div>

                <div className="card-content">
                  <div className="avatar-section">
                    <div className="avatar-preview">
                      <img src={profile.avatar} alt="Profile Avatar" />
                    </div>
                    {editable && (
                      <>
                        <Button
                          className="change-avatar"
                          onClick={triggerFileInput}
                        >
                          <FaCamera /> Change Avatar
                        </Button>
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleAvatarChange}
                          accept="image/*"
                          style={{ display: 'none' }}
                        />
                      </>
                    )}
                  </div>

                  <div className="info-section">
                    <Form onSubmit={saveProfile}>
                      <Form.Group className="form-group-custom">
                        <Form.Label>
                          <FaUser className="input-icon" /> Full Name
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={profile.name}
                          onChange={handleProfileChange}
                          disabled={!editable}
                          required
                        />
                      </Form.Group>

                      <Form.Group className="form-group-custom">
                        <Form.Label>
                          <FaEnvelope className="input-icon" /> Email Address
                        </Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={profile.email}
                          disabled
                        />
                      </Form.Group>

                      <Form.Group className="form-group-custom">
                        <Form.Label>
                          <FaPhone className="input-icon" /> Phone Number
                        </Form.Label>
                        <Form.Control
                          type="tel"
                          name="phone"
                          value={profile.phone}
                          onChange={handleProfileChange}
                          disabled={!editable}
                        />
                      </Form.Group>
                      {!editable && (
                        <Button
                          className="edit-profile"
                          size="sm"
                          onClick={toggleEdit}
                        >
                          <FaEdit /> Edit Profile
                        </Button>
                      )}
                      {editable && (
                        <div className="form-actions">
                          <Button
                            className="save-profile"
                            type="submit"
                            disabled={isLoading}
                          >
                            {isLoading ? 'Saving...' : 'Save Changes'}
                          </Button>
                          <Button
                            className="cancel-profile"
                            variant="secondary"
                            onClick={cancelEdit}
                            disabled={isLoading}
                            style={{ marginLeft: '10px' }}
                          >
                            <FaTimes /> Cancel
                          </Button>
                        </div>
                      )}
                    </Form>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'password' && (
              <div className="profile-card">
                <div className="card-header">
                  <h3>Change Password</h3>
                  <p>Secure your account with a new password</p>
                </div>
                {alerts.password && (
                  <Alert
                    variant={alerts.password.variant}
                    className="alert-custom"
                  >
                    {alerts.password.variant === 'success' ? (
                      <FaCheck />
                    ) : (
                      <FaExclamationTriangle />
                    )}
                    {alerts.password.message}
                  </Alert>
                )}
                <div className="card-content password-content">
                  <Form onSubmit={savePassword}>
                    <Form.Group className="form-group-custom">
                      <Form.Label>
                        <FaLock className="input-icon" /> Current Password
                      </Form.Label>
                      <Form.Control
                        type="password"
                        name="current"
                        value={password.current}
                        onChange={handlePasswordChange}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="form-group-custom">
                      <Form.Label>
                        <FaLock className="input-icon" /> New Password
                      </Form.Label>
                      <Form.Control
                        type="password"
                        name="new"
                        value={password.new}
                        onChange={handlePasswordChange}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="form-group-custom">
                      <Form.Label>
                        <FaLock className="input-icon" /> Confirm New Password
                      </Form.Label>
                      <Form.Control
                        type="password"
                        name="confirm"
                        value={password.confirm}
                        onChange={handlePasswordChange}
                        required
                      />
                    </Form.Group>

                    <div className="form-actions">
                      <Button
                        variant="warning"
                        type="submit"
                        disabled={isLoading}
                      >
                        {isLoading ? 'Updating...' : 'Change Password'}
                      </Button>
                    </div>
                  </Form>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default withAuth(ClientProfile, 'client');
